import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../../services/authService';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCGUn8Qpxc8UJIigEdDvOu3IgEAZIvTwPI';

const esInternacional = (val) => val.replace(/\D/g, '').startsWith('00');

const formatTel = (val) => {
  const n = val.replace(/\D/g, '');
  if (n.startsWith('00')) return n.slice(0, 15); // internacional: máx 15 dígitos, sin formateo extra
  const d = n.slice(0, 9);
  if (d.length >= 6) return `${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6)}`;
  if (d.length >= 3) return `${d.slice(0,3)} ${d.slice(3)}`;
  return d;
};

const Spinner = ({ sm }) => (
  <div className={`${sm ? 'w-3.5 h-3.5 border' : 'w-4 h-4 border-2'} border-current border-t-transparent rounded-full animate-spin flex-shrink-0`} />
);

const estadoOrden = (state) => {
  if (!state) return { label: '—', cls: 'bg-gray-100 text-gray-400' };
  const s = state.toLowerCase();
  if (s.includes('terminado') || s.includes('cerrado') || s.includes('facturado'))
    return { label: state, cls: 'bg-emerald-100 text-emerald-700' };
  if (s.includes('garantía') || s.includes('garantia'))
    return { label: state, cls: 'bg-amber-100 text-amber-700' };
  if (s.includes('pendiente') || s.includes('pend'))
    return { label: state, cls: 'bg-blue-100 text-blue-700' };
  return { label: state, cls: 'bg-gray-100 text-gray-500' };
};

const formatFecha = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const initials = (name) =>
  (name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

const AVATAR_COLORS = [
  'bg-red-100 text-red-600', 'bg-blue-100 text-blue-600',
  'bg-emerald-100 text-emerald-600', 'bg-violet-100 text-violet-600',
  'bg-amber-100 text-amber-600',
];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// ── Campo de formulario ───────────────────────────────────────────────────────
const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
      {label}
      {required && <span className="text-red-400 font-bold">*</span>}
      {hint && <span className="text-gray-300 font-normal normal-case tracking-normal ml-1">— {hint}</span>}
    </label>
    {children}
  </div>
);

// ── Componente principal ──────────────────────────────────────────────────────
const StepClientSearch = ({ formData, updateFormData, goToNextStep }) => {
  const initEstado = () => {
    if (formData.clienteVerificado && formData.addressId) return 'encontrado';
    if (formData.clienteId === false && formData.nombreCompleto) return 'nuevo';
    return 'idle';
  };

  const [telefono, setTelefono] = useState(
    formData.telefono ? formatTel(formData.telefono.replace(/\s/g, '')) : ''
  );
  const [buscando, setBuscando] = useState(false);
  const [estado, setEstado] = useState(initEstado);
  const [error, setError] = useState('');
  const [ordenes, setOrdenes] = useState([]);
  const [cargandoOrdenes, setCargandoOrdenes] = useState(false);
  const [creandoCliente, setCreandoCliente] = useState(false);
  const [errorCreacion, setErrorCreacion] = useState('');
  const [mapsReady, setMapsReady] = useState(false);

  const [nuevo, setNuevo] = useState({
    nombreCompleto: formData.nombreCompleto || '',
    email: formData.email || '',
    direccion: formData.direccion || '',
    codigoPostal: formData.codigoPostal || '',
    localidad: formData.localidad || '',
    provincia: formData.provincia || '',
  });

  const telefonoRef = useRef(null);
  const direccionRef = useRef(null);
  const autocompleteRef = useRef(null);
  const nombreInputRef = useRef(null);

  const [buscarPor, setBuscarPor] = useState('telefono');
  const [busquedaNombre, setBusquedaNombre] = useState('');
  const [resultadosNombre, setResultadosNombre] = useState([]);
  const [buscandoNombre, setBuscandoNombre] = useState(false);

  const [editandoCliente, setEditandoCliente] = useState(false);
  const [editData, setEditData] = useState({});
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState('');

  // Estado para cliente sin dirección
  const [clienteSinDir, setClienteSinDir] = useState(null);
  const [nuevaDir, setNuevaDir] = useState({ direccion: '', codigoPostal: '', localidad: '', provincia: '' });
  const [creandoDir, setCreandoDir] = useState(false);
  const [errorDir, setErrorDir] = useState('');

  // Focus en teléfono al entrar en idle
  useEffect(() => {
    if (estado === 'idle') setTimeout(() => telefonoRef.current?.focus(), 80);
  }, [estado]);

  // Cargar historial si ya viene con cliente
  useEffect(() => {
    if (estado === 'encontrado' && formData.clienteId) cargarOrdenes(formData.clienteId);
  }, []); // eslint-disable-line

  // ── Google Maps Places ────────────────────────────────────────────────────

  const initAutocomplete = useCallback(() => {
    if (!direccionRef.current || !window.google?.maps?.places) return;
    if (autocompleteRef.current) return; // ya inicializado

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        direccionRef.current,
        { types: ['address'], componentRestrictions: { country: 'ES' }, fields: ['address_components', 'formatted_address'], language: 'es' }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.address_components) return;

        const get = (type, short = false) => {
          const c = place.address_components.find(x => x.types.includes(type));
          return c ? (short ? c.short_name : c.long_name) : '';
        };

        const street = [get('route'), get('street_number')].filter(Boolean).join(', ')
          || place.formatted_address?.split(',')[0]?.trim() || '';

        setNuevo(p => ({
          ...p,
          direccion: street,
          codigoPostal: get('postal_code'),
          localidad: get('locality') || get('administrative_area_level_2'),
          provincia: get('administrative_area_level_2') || get('administrative_area_level_1'),
        }));
      });
    } catch (e) {
      console.error('Autocomplete init error:', e);
    }
  }, []);

  // Cargar el script de Maps la primera vez que se muestra el form nuevo
  useEffect(() => {
    if (estado !== 'nuevo') return;

    if (window.google?.maps?.places) {
      setMapsReady(true);
      return;
    }

    // Evitar cargar el script dos veces
    if (document.querySelector('script[data-gmaps]')) {
      const check = setInterval(() => {
        if (window.google?.maps?.places) {
          setMapsReady(true);
          clearInterval(check);
        }
      }, 200);
      return () => clearInterval(check);
    }

    const script = document.createElement('script');
    script.setAttribute('data-gmaps', '1');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places&language=es&region=ES`;
    script.async = true;
    script.onload = () => setMapsReady(true);
    document.head.appendChild(script);
  }, [estado]);

  // Inicializar autocomplete cuando el input esté listo y Maps cargado
  useEffect(() => {
    if (mapsReady && direccionRef.current) {
      setTimeout(() => initAutocomplete(), 50);
    }
  }, [mapsReady, initAutocomplete]);

  // Cleanup al desmontar
  useEffect(() => () => {
    if (autocompleteRef.current) {
      window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  }, []);

  // ── Lógica ────────────────────────────────────────────────────────────────

  const cargarOrdenes = async (clienteId) => {
    setCargandoOrdenes(true);
    try {
      const res = await fetch(`/.netlify/functions/getClientOrders?clientId=${clienteId}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.success) setOrdenes(data.orders || []);
    } catch { /* silencioso */ }
    finally { setCargandoOrdenes(false); }
  };

  const buscar = async () => {
    const limpio = telefono.replace(/\s/g, '');
    const intl = esInternacional(limpio);
    if (!intl && !/^[679]\d{8}$/.test(limpio)) {
      setError('Número no válido — empieza por 6, 7 o 9, o por 00 para números extranjeros');
      return;
    }
    if (intl && !/^00\d{7,13}$/.test(limpio)) {
      setError('Número internacional no válido — formato: 00 + prefijo + número (ej. 0045xxxxxxxx)');
      return;
    }
    setBuscando(true);
    setError('');

    try {
      const res = await fetch(`/.netlify/functions/searchClientByPhone?phone=${encodeURIComponent(limpio)}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error en la búsqueda');

      if (data.client) {
        const c = data.client;
        const addr = c['main-address'];
        const addressId = addr ? (addr.id || addr['address-id'] || addr.addressId) : null;

        if (!addressId) {
          // Cliente encontrado pero sin dirección — pedir al operador que la añada
          setClienteSinDir({
            id: c.id,
            nombre: c['legal-name'] || c.name || '',
            telefono: c.phone || limpio,
            email: c.email || '',
            fullReference: c['full-reference'] || '',
          });
          updateFormData({ telefono: limpio });
          setEstado('sin_direccion');
          return;
        }

        updateFormData({
          nombreCompleto: c['legal-name'] || c.name || '',
          telefono: c.phone || limpio,
          email: c.email || '',
          direccion: addr['address-data'] || '',
          codigoPostal: addr['postal-code'] || '',
          localidad: addr['city-town'] || '',
          clienteVerificado: true,
          clienteId: c.id,
          addressId,
          clienteFullReference: c['full-reference'] || '',
        });
        setEstado('encontrado');
        cargarOrdenes(c.id);
      } else {
        updateFormData({ telefono: limpio, clienteVerificado: false });
        setEstado('nuevo');
      }
    } catch (e) {
      setError(e.message);
      setEstado('idle');
    } finally {
      setBuscando(false);
    }
  };

  // Auto-búsqueda cuando se llega con teléfono pre-rellenado desde CRM Email
  const autoSearched = useRef(false);
  useEffect(() => {
    if (autoSearched.current || !telefono || estado !== 'idle') return;
    autoSearched.current = true;
    buscar();
  }, []); // eslint-disable-line

  const crearCliente = async () => {
    const faltantes = ['nombreCompleto', 'direccion', 'codigoPostal', 'localidad', 'provincia']
      .filter(k => !nuevo[k]?.trim());
    if (faltantes.length) { setErrorCreacion('Completa todos los campos obligatorios'); return; }

    setCreandoCliente(true);
    setErrorCreacion('');

    try {
      const telLimpio = telefono.replace(/\s/g, '');
      const res = await fetch('/.netlify/functions/createClient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ ...nuevo, telefono: telLimpio }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Error creando cliente');

      updateFormData({
        ...nuevo,
        telefono: telLimpio,
        clienteVerificado: true,
        clienteId: result.client.id,
        addressId: result.client.mainAddressId,
        clienteFullReference: result.client.fullReference || '',
      });
      setEstado('registrado');
    } catch (e) {
      setErrorCreacion(e.message);
    } finally {
      setCreandoCliente(false);
    }
  };

  const resetear = () => {
    setEstado('idle');
    setOrdenes([]);
    setError('');
    setErrorCreacion('');
    setResultadosNombre([]);
    setBusquedaNombre('');
    setBuscarPor('telefono');
    setEditandoCliente(false);
    setErrorEdicion('');
    setClienteSinDir(null);
    setNuevaDir({ direccion: '', codigoPostal: '', localidad: '', provincia: '' });
    setErrorDir('');
    autocompleteRef.current = null;
    updateFormData({
      telefono: '', clienteVerificado: false, clienteId: null, addressId: null,
      nombreCompleto: '', email: '', direccion: '', codigoPostal: '', localidad: '', provincia: '',
    });
    setTimeout(() => telefonoRef.current?.focus(), 60);
  };

  const guardarNuevaDireccion = async () => {
    const { direccion, codigoPostal, localidad, provincia } = nuevaDir;
    if (!direccion.trim() || !codigoPostal.trim() || !localidad.trim()) {
      setErrorDir('Dirección, código postal y localidad son obligatorios');
      return;
    }
    setCreandoDir(true);
    setErrorDir('');
    try {
      const res = await fetch('/.netlify/functions/createAddress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          addressData: direccion.trim(),
          postalCode: codigoPostal.trim(),
          cityTown: localidad.trim(),
          province: provincia.trim(),
        }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Error creando dirección');

      updateFormData({
        nombreCompleto: clienteSinDir.nombre,
        telefono: clienteSinDir.telefono,
        email: clienteSinDir.email,
        direccion: direccion.trim(),
        codigoPostal: codigoPostal.trim(),
        localidad: localidad.trim(),
        provincia: provincia.trim(),
        clienteVerificado: true,
        clienteId: clienteSinDir.id,
        addressId: result.address.id,
        clienteFullReference: clienteSinDir.fullReference,
      });
      setEstado('encontrado');
      cargarOrdenes(clienteSinDir.id);
    } catch (e) {
      setErrorDir(e.message);
    } finally {
      setCreandoDir(false);
    }
  };

  const buscarPorNombre = async () => {
    const q = busquedaNombre.trim();
    if (q.length < 2) return;
    setBuscandoNombre(true);
    setError('');
    setResultadosNombre([]);
    try {
      const res = await fetch(`/.netlify/functions/searchClienteStelOrder?q=${encodeURIComponent(q)}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error en la búsqueda');
      if (!data.results?.length) {
        setError('No se encontraron clientes con ese nombre');
      } else {
        setResultadosNombre(data.results);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBuscandoNombre(false);
    }
  };

  const seleccionarClienteNombre = (r) => {
    const addressId = r.addressId || r.raw?.['main-address']?.id || r.raw?.['main-address']?.['address-id'];
    if (!addressId) {
      setClienteSinDir({
        id: r.id,
        nombre: r.clienteNombre,
        telefono: r.phone || '',
        email: r.email || '',
        fullReference: r.raw?.['full-reference'] || '',
      });
      setTelefono(formatTel((r.phone || '').replace(/\s/g, '')));
      updateFormData({ telefono: r.phone || '' });
      setEstado('sin_direccion');
      setResultadosNombre([]);
      return;
    }
    setTelefono(formatTel((r.phone || '').replace(/\s/g, '')));
    updateFormData({
      nombreCompleto: r.clienteNombre,
      telefono: r.phone || '',
      email: r.email || '',
      direccion: r.direccion || '',
      codigoPostal: r.codigoPostal || '',
      localidad: r.municipio || '',
      clienteVerificado: true,
      clienteId: r.id,
      addressId,
      clienteFullReference: r.raw?.['full-reference'] || '',
    });
    setEstado('encontrado');
    setResultadosNombre([]);
    cargarOrdenes(r.id);
  };

  const guardarEdicion = async () => {
    const faltantes = ['nombreCompleto', 'direccion', 'codigoPostal', 'localidad']
      .filter(k => !editData[k]?.trim());
    if (faltantes.length) { setErrorEdicion('Completa todos los campos obligatorios'); return; }
    setGuardandoEdicion(true);
    setErrorEdicion('');
    try {
      const res = await fetch('/.netlify/functions/updateClient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ clientId: formData.clienteId, addressId: formData.addressId, ...editData }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Error actualizando cliente');
      updateFormData({
        nombreCompleto: editData.nombreCompleto,
        email: editData.email,
        direccion: editData.direccion,
        codigoPostal: editData.codigoPostal,
        localidad: editData.localidad,
      });
      setEditandoCliente(false);
    } catch (e) {
      setErrorEdicion(e.message);
    } finally {
      setGuardandoEdicion(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">

      {/* Step header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">👤</span>
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900 leading-tight">Identificación del cliente</h2>
            <p className="text-xs text-gray-400 mt-0.5">Busca por teléfono · Enter para buscar</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

        {/* ── Modo de búsqueda ── */}
        {(estado === 'idle' || estado === 'nuevo') && (
          <div className="flex gap-1">
            <button
              onClick={() => { setBuscarPor('telefono'); setResultadosNombre([]); setError(''); }}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                buscarPor === 'telefono' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >📞 Teléfono</button>
            <button
              onClick={() => { setBuscarPor('nombre'); setError(''); setTimeout(() => nombreInputRef.current?.focus(), 80); }}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                buscarPor === 'nombre' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >👤 Nombre</button>
          </div>
        )}

        {/* ── Buscador ── */}
        <div>
          {buscarPor === 'nombre' && (estado === 'idle' || estado === 'nuevo') ? (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                Nombre del cliente
              </label>
              <div className="flex gap-2.5">
                <input
                  ref={nombreInputRef}
                  type="text"
                  className="flex-1 px-4 py-3 text-base font-medium bg-white border-2 border-gray-200 focus:border-red-500 rounded-xl outline-none placeholder-gray-300 transition-colors"
                  placeholder="Nombre, apellidos o razón social..."
                  value={busquedaNombre}
                  onChange={e => { setBusquedaNombre(e.target.value); setError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); buscarPorNombre(); } }}
                />
                <button
                  onClick={buscarPorNombre}
                  disabled={buscandoNombre || busquedaNombre.trim().length < 2}
                  className="px-4 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white rounded-xl font-semibold flex items-center gap-2 transition-colors whitespace-nowrap shadow-sm"
                >
                  {buscandoNombre ? <Spinner /> : <span className="text-sm">🔍</span>}
                  <span className="hidden sm:inline text-sm">{buscandoNombre ? 'Buscando…' : 'Buscar'}</span>
                </button>
              </div>
              {resultadosNombre.length > 0 && (
                <div className="mt-3 bg-white rounded-xl border overflow-hidden shadow-sm">
                  {resultadosNombre.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => seleccionarClienteNombre(r)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between gap-3 border-b last:border-b-0 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-gray-800 truncate">{r.clienteNombre}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {r.phone && <span>📞 {r.phone}</span>}
                          {r.municipio && <span className="ml-2">📍 {r.municipio}</span>}
                        </p>
                      </div>
                      <span className="text-gray-300 flex-shrink-0 text-sm">→</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                Teléfono
              </label>
              <div className="flex gap-2.5">
                {(() => {
                  const intl = esInternacional(telefono);
                  return (
                    <div className={`flex flex-1 rounded-xl overflow-hidden border-2 transition-colors ${
                      error ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-red-500 bg-white'
                    }`}>
                      <div className="bg-gray-50 border-r border-gray-200 px-3 flex items-center gap-1.5 flex-shrink-0 min-w-[64px]">
                        {intl ? (
                          <span className="text-sm font-bold text-blue-500">🌍 Intl</span>
                        ) : (
                          <>
                            <span className="text-base">🇪🇸</span>
                            <span className="text-sm font-bold text-gray-400">+34</span>
                          </>
                        )}
                      </div>
                      <input
                        ref={telefonoRef}
                        type="tel"
                        className="flex-1 px-4 py-3 text-lg font-semibold bg-transparent outline-none placeholder-gray-300 disabled:text-gray-400"
                        placeholder="600 000 000 · ó 0045..."
                        value={telefono}
                        onChange={e => { setTelefono(formatTel(e.target.value)); setError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); buscar(); } }}
                        disabled={buscando || estado === 'encontrado' || estado === 'registrado'}
                        maxLength={intl ? 15 : 11}
                        autoComplete="off"
                      />
                    </div>
                  );
                })()}

                {estado === 'idle' || estado === 'nuevo' ? (
                  <button
                    onClick={buscar}
                    disabled={buscando || (() => {
                      const n = telefono.replace(/\s/g, '');
                      return esInternacional(n) ? n.length < 9 : n.length < 9;
                    })()}
                    className="px-4 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-40 text-white rounded-xl font-semibold flex items-center gap-2 transition-colors whitespace-nowrap shadow-sm"
                  >
                    {buscando ? <Spinner /> : <span className="text-sm">🔍</span>}
                    <span className="hidden sm:inline text-sm">{buscando ? 'Buscando…' : 'Buscar'}</span>
                  </button>
                ) : (
                  <button
                    onClick={resetear}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-xl font-medium transition-colors whitespace-nowrap text-sm border border-gray-200"
                  >
                    ✕ Otro
                  </button>
                )}
              </div>
            </div>
          )}
          {error && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <span>⚠️</span> {error}
            </p>
          )}
        </div>

        {/* ── CLIENTE ENCONTRADO ── */}
        {estado === 'encontrado' && (
          <div className="space-y-3">

            {/* Client card */}
            <div className="rounded-xl border-2 border-emerald-200 overflow-hidden bg-white">
              <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Cliente verificado</span>
                </div>
                <div className="flex items-center gap-2">
                  {formData.clienteFullReference && (
                    <span className="text-xs font-mono text-emerald-500 bg-white border border-emerald-200 px-2 py-0.5 rounded-md">
                      {formData.clienteFullReference}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setEditData({
                        nombreCompleto: formData.nombreCompleto || '',
                        email: formData.email || '',
                        direccion: formData.direccion || '',
                        codigoPostal: formData.codigoPostal || '',
                        localidad: formData.localidad || '',
                      });
                      setEditandoCliente(e => !e);
                      setErrorEdicion('');
                    }}
                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-800 flex items-center gap-1 transition-colors"
                  >
                    ✏️ Editar
                  </button>
                </div>
              </div>

              <div className="p-4 flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${avatarColor(formData.nombreCompleto)}`}>
                  <span className="text-lg font-bold">{initials(formData.nombreCompleto)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm">{formData.nombreCompleto}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="text-xs text-gray-500">📞 {formData.telefono || telefono.replace(/\s/g,'')}</span>
                    {formData.email && <span className="text-xs text-gray-500">✉️ {formData.email}</span>}
                  </div>
                  {formData.direccion && (
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      📍 {formData.direccion}{formData.codigoPostal ? `, ${formData.codigoPostal}` : ''} {formData.localidad}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Formulario de edición */}
            {editandoCliente && (
              <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3.5">
                <h3 className="text-sm font-bold text-gray-700">✏️ Editar datos del cliente</h3>

                <Field label="Nombre completo" required>
                  <input type="text" className="crm-input" value={editData.nombreCompleto}
                    onChange={e => setEditData(p => ({ ...p, nombreCompleto: e.target.value }))}
                    onKeyDown={e => e.stopPropagation()} />
                </Field>
                <Field label="Email" hint="opcional">
                  <input type="email" className="crm-input" value={editData.email}
                    onChange={e => setEditData(p => ({ ...p, email: e.target.value }))}
                    onKeyDown={e => e.stopPropagation()} />
                </Field>
                <Field label="Dirección" required>
                  <input type="text" className="crm-input" value={editData.direccion}
                    onChange={e => setEditData(p => ({ ...p, direccion: e.target.value }))}
                    onKeyDown={e => e.stopPropagation()} />
                </Field>
                <div className="grid grid-cols-5 gap-2">
                  <div className="col-span-2">
                    <Field label="C. Postal" required>
                      <input type="text" className="crm-input" value={editData.codigoPostal} maxLength={5}
                        onChange={e => setEditData(p => ({ ...p, codigoPostal: e.target.value }))}
                        onKeyDown={e => e.stopPropagation()} />
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Field label="Localidad" required>
                      <input type="text" className="crm-input" value={editData.localidad}
                        onChange={e => setEditData(p => ({ ...p, localidad: e.target.value }))}
                        onKeyDown={e => e.stopPropagation()} />
                    </Field>
                  </div>
                </div>

                {errorEdicion && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-700 flex items-center gap-2">
                    <span>⚠️</span> {errorEdicion}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={guardarEdicion}
                    disabled={guardandoEdicion}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl font-bold text-sm transition-colors"
                  >
                    {guardandoEdicion ? 'Guardando…' : '💾 Guardar cambios'}
                  </button>
                  <button
                    onClick={() => { setEditandoCliente(false); setErrorEdicion(''); }}
                    className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-medium text-sm transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Historial */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Historial de órdenes</span>
                {cargandoOrdenes && <Spinner sm />}
              </div>
              <div className="divide-y divide-gray-50">
                {!cargandoOrdenes && ordenes.length === 0 && (
                  <p className="text-sm text-gray-400 py-5 text-center">Sin órdenes previas</p>
                )}
                {ordenes.slice(0, 5).map((o, i) => {
                  const st = estadoOrden(o.state);
                  const hasPdf = !!o.pdfPath;
                  return (
                    <div
                      key={i}
                      onClick={() => hasPdf && window.open(o.pdfPath, '_blank', 'noopener')}
                      className={`px-4 py-2.5 flex items-center justify-between gap-3 transition-colors ${
                        hasPdf ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100' : ''
                      }`}
                      title={hasPdf ? 'Abrir PDF' : undefined}
                    >
                      <div className="min-w-0 flex items-center gap-2">
                        {hasPdf && (
                          <span className="text-gray-300 flex-shrink-0 text-base">📄</span>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800">{o.reference}</p>
                          <p className="text-xs text-gray-400">{formatFecha(o.date)}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={goToNextStep}
              className="w-full py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md"
            >
              Continuar con este cliente
              <span>→</span>
            </button>
          </div>
        )}

        {/* ── CLIENTE NUEVO ── */}
        {/* ── CLIENTE SIN DIRECCIÓN ── */}
        {estado === 'sin_direccion' && clienteSinDir && (
          <div className="space-y-3">

            <div className="rounded-xl border px-4 py-3 flex items-center gap-3 bg-amber-50 border-amber-200">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${avatarColor(clienteSinDir.nombre)}`}>
                <span className="text-base font-bold">{initials(clienteSinDir.nombre)}</span>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm text-amber-800 truncate">{clienteSinDir.nombre}</p>
                <p className="text-xs text-amber-600 mt-0.5">⚠️ Sin dirección registrada — añádela para continuar</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3.5">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Dirección del cliente</p>

              <Field label="Dirección" required>
                <input
                  type="text"
                  className="crm-input"
                  placeholder="Calle y número"
                  value={nuevaDir.direccion}
                  onChange={e => setNuevaDir(p => ({ ...p, direccion: e.target.value }))}
                  onKeyDown={e => e.stopPropagation()}
                  autoFocus
                />
              </Field>

              <div className="grid grid-cols-5 gap-2">
                <div className="col-span-2">
                  <Field label="C. Postal" required>
                    <input
                      type="text"
                      className="crm-input"
                      placeholder="03001"
                      maxLength={5}
                      value={nuevaDir.codigoPostal}
                      onChange={e => setNuevaDir(p => ({ ...p, codigoPostal: e.target.value }))}
                      onKeyDown={e => e.stopPropagation()}
                    />
                  </Field>
                </div>
                <div className="col-span-3">
                  <Field label="Localidad" required>
                    <input
                      type="text"
                      className="crm-input"
                      placeholder="Ciudad"
                      value={nuevaDir.localidad}
                      onChange={e => setNuevaDir(p => ({ ...p, localidad: e.target.value }))}
                      onKeyDown={e => e.stopPropagation()}
                    />
                  </Field>
                </div>
              </div>

              <Field label="Provincia" hint="opcional">
                <input
                  type="text"
                  className="crm-input"
                  placeholder="Alicante, Murcia…"
                  value={nuevaDir.provincia}
                  onChange={e => setNuevaDir(p => ({ ...p, provincia: e.target.value }))}
                  onKeyDown={e => e.stopPropagation()}
                />
              </Field>

              {errorDir && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-700 flex items-center gap-2">
                  <span>⚠️</span> {errorDir}
                </div>
              )}

              <button
                onClick={guardarNuevaDireccion}
                disabled={creandoDir}
                className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm"
              >
                {creandoDir ? <><Spinner /> Guardando dirección…</> : '📍 Guardar dirección y continuar'}
              </button>
            </div>
          </div>
        )}

        {(estado === 'nuevo' || estado === 'registrado') && (
          <div className="space-y-3">

            <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${
              estado === 'registrado' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
            }`}>
              <span className="text-xl">{estado === 'registrado' ? '✅' : '⚠️'}</span>
              <div>
                <p className={`font-bold text-sm ${estado === 'registrado' ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {estado === 'registrado' ? 'Cliente registrado' : 'Cliente nuevo — no encontrado'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {estado === 'registrado' ? 'Cliente creado correctamente en el sistema.' : 'Rellena los datos para registrarlo.'}
                </p>
              </div>
            </div>

            {estado === 'nuevo' && (
              <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3.5">

                <Field label="Nombre completo" required>
                  <input
                    type="text"
                    className="crm-input"
                    placeholder="Nombre y apellidos o razón social"
                    value={nuevo.nombreCompleto}
                    onChange={e => setNuevo(p => ({ ...p, nombreCompleto: e.target.value }))}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </Field>

                <Field label="Email" hint="opcional">
                  <input
                    type="email"
                    className="crm-input"
                    placeholder="cliente@ejemplo.com"
                    value={nuevo.email}
                    onChange={e => setNuevo(p => ({ ...p, email: e.target.value }))}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </Field>

                <Field label="Dirección" required hint={mapsReady ? '🌍 Autocompletado con Google' : ''}>
                  <div className="relative">
                    <input
                      ref={direccionRef}
                      type="text"
                      className="crm-input pr-8"
                      placeholder="Calle y número"
                      value={nuevo.direccion}
                      onChange={e => setNuevo(p => ({ ...p, direccion: e.target.value }))}
                      onKeyDown={e => e.stopPropagation()}
                    />
                    {mapsReady && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" title="Autocompletado activo">🗺️</span>
                    )}
                  </div>
                </Field>

                <div className="grid grid-cols-5 gap-2">
                  <div className="col-span-2">
                    <Field label="C. Postal" required>
                      <input
                        type="text"
                        className="crm-input"
                        placeholder="03001"
                        maxLength={5}
                        value={nuevo.codigoPostal}
                        onChange={e => setNuevo(p => ({ ...p, codigoPostal: e.target.value }))}
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Field label="Localidad" required>
                      <input
                        type="text"
                        className="crm-input"
                        placeholder="Ciudad"
                        value={nuevo.localidad}
                        onChange={e => setNuevo(p => ({ ...p, localidad: e.target.value }))}
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </Field>
                  </div>
                </div>

                <Field label="Provincia" required>
                  <input
                    type="text"
                    className="crm-input"
                    placeholder="Alicante, Murcia, Madrid…"
                    value={nuevo.provincia}
                    onChange={e => setNuevo(p => ({ ...p, provincia: e.target.value }))}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </Field>

                {errorCreacion && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-700 flex items-center gap-2">
                    <span>⚠️</span> {errorCreacion}
                  </div>
                )}

                <button
                  onClick={crearCliente}
                  disabled={creandoCliente}
                  className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                  {creandoCliente ? <><Spinner /> Registrando…</> : '👤 Registrar cliente y continuar'}
                </button>
              </div>
            )}

            {estado === 'registrado' && (
              <button
                onClick={goToNextStep}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md"
              >
                Continuar <span>→</span>
              </button>
            )}
          </div>
        )}

      </div>{/* /body */}

      <style>{`
        .crm-input {
          width: 100%;
          padding: 0.6rem 0.875rem;
          border: 1.5px solid #e5e7eb;
          border-radius: 0.625rem;
          font-size: 0.9375rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          background: white;
          color: #111827;
        }
        .crm-input:focus {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220,38,38,0.08);
        }
        /* Ocultar atribución de Google dentro del pac-container si interfiere */
        .pac-container { z-index: 9999 !important; }
      `}</style>
    </div>
  );
};

export default StepClientSearch;
